// src/pages/PrincipalRegistration.js
// v5 – softer registration copy + clearer success handoff
//
// Changes vs v4:
// ✅ Rewords page to feel guided, not administrative
// ✅ Keeps registration-only scope (no onboarding fields here)
// ✅ Adds dedicated post-submit success state
// ✅ Changes CTA from "Register" to "Continue"
// ✅ Keeps roll number lookup + email/website confirmation intact

import React, { useState, useEffect, useMemo } from "react";

import api from "../services/api";
import { API_PATHS } from "../config";
import "../styles/PrincipalRegistration.css";

const PrincipalRegistration = () => {
  const [rollNumber, setRollNumber] = useState("");
  const [schoolData, setSchoolData] = useState(null);

  const [message, setMessage] = useState("");
  const [principalEmail, setPrincipalEmail] = useState("");
  const [isRegistered, setIsRegistered] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [schoolWebsite, setSchoolWebsite] = useState("");
  const [email, setEmail] = useState("");

  const [prefilledEmail, setPrefilledEmail] = useState("");
  const [prefilledWebsite, setPrefilledWebsite] = useState("");

  const [emailTouched, setEmailTouched] = useState(false);
  const [websiteTouched, setWebsiteTouched] = useState(false);

  useEffect(() => {
    if (message) window.scrollTo({ top: 0, behavior: "smooth" });
  }, [message]);

  const fetchSchoolData = async (cleanedRollNumber) => {
    if (!cleanedRollNumber) {
      setSchoolData(null);
      setPrincipalEmail("");
      setEmail("");
      setSchoolWebsite("");
      setPrefilledEmail("");
      setPrefilledWebsite("");
      setEmailTouched(false);
      setWebsiteTouched(false);
      setMessage("");
      return;
    }

    try {
      const { data } = await api.get(API_PATHS.GET_SCHOOL_DATA(cleanedRollNumber));
      setSchoolData(data);

      const nextEmail = data.principal_email || "";
      const nextWebsite = data.school_website || "";

      setPrincipalEmail(nextEmail);
      setEmail(nextEmail);
      setSchoolWebsite(nextWebsite);

      setPrefilledEmail(nextEmail);
      setPrefilledWebsite(nextWebsite);

      setEmailTouched(false);
      setWebsiteTouched(false);
      setMessage("");
    } catch (error) {
      setSchoolData(null);
      setPrincipalEmail("");
      setEmail("");
      setSchoolWebsite("");
      setPrefilledEmail("");
      setPrefilledWebsite("");
      setEmailTouched(false);
      setWebsiteTouched(false);
      setMessage("School not found. Please check the roll number.");
    }
  };

  const handleRegister = async () => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    if (submitting) return;

    if (!/\S+@\S+\.\S+/.test(email)) {
      setMessage("Please enter a valid email address.");
      return;
    }

    if (!schoolWebsite.trim()) {
      setMessage("Please enter your school website.");
      return;
    }

    const payload = {
      email: email.trim(),
      role: "PRINCIPAL",
      roll_number: rollNumber.trim(),
      school_website: schoolWebsite.trim(),
    };

    setSubmitting(true);
    setMessage("");

    try {
      await api.post(API_PATHS.PRINCIPAL_REGISTRATION, payload);
      setMessage("");
      setIsRegistered(true);
    } catch (error) {
      setMessage(error?.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const emailIsPrefilled = useMemo(() => {
    if (!prefilledEmail) return false;
    if (emailTouched) return false;
    return email === prefilledEmail;
  }, [email, prefilledEmail, emailTouched]);

  const websiteIsPrefilled = useMemo(() => {
    if (!prefilledWebsite) return false;
    if (websiteTouched) return false;
    return schoolWebsite === prefilledWebsite;
  }, [schoolWebsite, prefilledWebsite, websiteTouched]);

  if (isRegistered) {
    return (
      <div className="principal-registration page">
        <div className="registration-card">
          <header className="registration-header">
            <h1 className="registration-title">Check your email</h1>
            <p className="registration-subtitle">
              We&apos;re setting things up for your school.
            </p>
          </header>

          <p className="message success" aria-live="polite">
            We&apos;ll email you when you&apos;re ready to continue.
          </p>

          <p className="registration-subtitle">
            While you wait, you can install the GhrianGhraffy App on your smartphone.
            Once your registration is approved, open the approval email on that phone for the smoothest setup.
            If you open the same email on a laptop or desktop, you can still continue in your browser.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="principal-registration page">
      <div className="registration-card">
        <header className="registration-header">
          <h1 className="registration-title">Get started with your school</h1>
          <p className="registration-subtitle">
            Find your school and confirm your details to get started.
          </p>
        </header>

        {message && (
          <p
            className={`message ${
              message.toLowerCase().includes("failed") || message.toLowerCase().includes("not found")
                ? "error"
                : "success"
            }`}
            aria-live="polite"
          >
            {message}
          </p>
        )}

        <section className="registration-section">
          <h2 className="section-title">Find your school</h2>

          <div className="form-group">
            <input
              id="roll-number"
              type="text"
              className="input-field"
              placeholder="School roll number"
              value={rollNumber}
              onChange={(e) => {
                const cleaned = e.target.value.trim();
                setRollNumber(cleaned);
                if (cleaned.length >= 5) fetchSchoolData(cleaned);
              }}
              onBlur={(e) => setRollNumber(e.target.value.trim())}
            />
          </div>

          {schoolData && (
            <div className="school-details">
              <p>
                <strong>School Name:</strong> {schoolData.official_name}
              </p>
              <p>
                <strong>Address:</strong> {schoolData.address_line_1}, {schoolData.county_description}
              </p>
              <p>
                <strong>Eircode:</strong> {schoolData.eircode}
              </p>
              <p>
                <strong>Phone:</strong> {schoolData.phone_no}
              </p>
              <p>
                <strong>Principal:</strong> {schoolData.principal_name}
              </p>
              <p>
                <strong>Principal Email:</strong> {principalEmail}
              </p>
              <p>
                <strong>Enrolment:</strong> {schoolData.enrolment_quantity}
              </p>
              {schoolData.school_website && (
                <p>
                  <strong>Website on file:</strong>{" "}
                  <a href={schoolData.school_website} target="_blank" rel="noopener noreferrer">
                    {schoolData.school_website}
                  </a>
                </p>
              )}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="school-website">School website</label>
            <span className="tooltip">You can update this if needed.</span>
            <input
              id="school-website"
              type="url"
              className={`input-field ${websiteIsPrefilled ? "is-prefilled" : ""}`}
              placeholder="https://www.yourschool.ie"
              value={schoolWebsite}
              onChange={(e) => {
                if (!websiteTouched) setWebsiteTouched(true);
                setSchoolWebsite(e.target.value);
              }}
              required
            />
          </div>
        </section>

        <section className="registration-section">
          <h2 className="section-title">Your email</h2>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              className={`input-field ${emailIsPrefilled ? "is-prefilled" : ""}`}
              placeholder="you@school.ie"
              value={email}
              onChange={(e) => {
                if (!emailTouched) setEmailTouched(true);
                setEmail(e.target.value);
              }}
              required
            />
            <span className="tooltip">Use the email address that&apos;s easiest for you to access.</span>
          </div>
        </section>

        <button
          className="register-button"
          onClick={handleRegister}
          disabled={
            submitting ||
            !rollNumber ||
            !email ||
            !schoolData ||
            !schoolWebsite.trim()
          }
        >
          {submitting ? "Submitting…" : "Continue"}
        </button>
      </div>
    </div>
  );
};

export default PrincipalRegistration;